import { Navigate, Route, Routes } from "react-router-dom";
import { SignOutButton, UserButton } from "@clerk/react";
import { Loader2, ShieldAlert } from "lucide-react";
import { useMe } from "@/context/MeContext";
import { FilesProvider } from "@/context/FilesContext";
import { CompanyProvider, useCompany } from "@/context/CompanyContext";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import Dashboard from "@/pages/Dashboard";
import Explorer from "@/pages/Explorer";
import Shared from "@/pages/Shared";
import SearchPage from "@/pages/SearchPage";
import Assistant from "@/pages/Assistant";
import Trash from "@/pages/Trash";
import Links from "@/pages/Links";
import Admin from "@/pages/Admin";
import { Logo } from "@/pages/AuthLayout";
import { OtpGate } from "@/components/auth/OtpGate";
import Validations from "@/pages/Validations";
import Suppliers from "@/pages/Suppliers";
import Account from "@/pages/Account";
import { PrivacyGate } from "@/components/auth/PrivacyGate";

function Pending({ me }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50" data-testid="account-pending">
      <div className="max-w-md w-full bg-white border rounded-xl p-8 text-center">
        <div className="flex justify-center mb-6"><Logo /></div>
        <ShieldAlert className="h-10 w-10 mx-auto text-amber-600" />
        <h1 className="font-display text-xl font-bold mt-4">Accès en attente</h1>
        <p className="text-sm text-slate-600 mt-2">
          Votre compte <strong>{me.email}</strong> doit être validé par un administrateur de SDR PNEUS 91, ou il a été désactivé.
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <UserButton />
          <SignOutButton><Button variant="outline" data-testid="pending-signout-btn">Se déconnecter</Button></SignOutButton>
        </div>
      </div>
    </div>
  );
}

export function ProtectedApp() {
  const { me, error } = useMe();
  if (error) return <div className="p-8 text-center text-sm">Impossible de charger votre profil. Actualisez la page.</div>;
  if (!me) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#1E3A8A]" /></div>;
  if (!me.active) return <Pending me={me} />;
  if (me.mfa_required && !me.mfa_verified) return <OtpGate />;
  return (
    <CompanyProvider>
      <FilesProvider>
        <AppShell>
          <PrivacyGate />
          <CompanyRoutes me={me} />
        </AppShell>
      </FilesProvider>
    </CompanyProvider>
  );
}

function CompanyRoutes({ me }) {
  const { current, companies } = useCompany();
  if (!companies.length) return <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#1E3A8A]" /></div>;
  return (
        <Routes key={current}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/fichiers" element={<Explorer />} />
          <Route path="/fichiers/:folderId" element={<Explorer />} />
          <Route path="/partages" element={<Shared />} />
          <Route path="/recherche" element={<SearchPage />} />
          <Route path="/assistant" element={<Assistant />} />
          <Route path="/validations" element={<Validations />} />
          <Route path="/fournisseurs" element={<Suppliers />} />
          <Route path="/fournisseurs/:supplierKey" element={<Suppliers />} />
          <Route path="/corbeille" element={<Trash />} />
          <Route path="/liens" element={<Links />} />
          <Route path="/compte" element={<Account />} />
          <Route path="/admin" element={me.role === "admin" ? <Admin /> : <Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
  );
}
